import { Link, useLocation } from "react-router-dom";
import { C } from "../styles/tokens";
import { isAppView, visitStorage } from "../utils/appView";

/**
 * Render inline markdown elements: **bold**, *italic*, [link](url), `code`.
 */
function renderInlineMarkdown(text, appView) {
  if (!text) return null;

  // Split tokens for bold, italic, code, and links
  const tokens = [];
  let remaining = text;
  let keyIndex = 0;

  while (remaining.length > 0) {
    // 1. Link: [label](url)
    const linkMatch = remaining.match(/^\[(.*?)\]\((.*?)\)/);
    if (linkMatch) {
      const [full, label, url] = linkMatch;
      let targetUrl = url;
      if (appView && (targetUrl.startsWith("/terms") || targetUrl.startsWith("/privacy"))) {
        targetUrl = targetUrl.includes("?")
          ? `${targetUrl}&view=app`
          : `${targetUrl}?view=app`;
      }
      tokens.push(
        targetUrl.startsWith("/") ? (
          <Link
            key={`l-${keyIndex++}`}
            to={targetUrl}
            style={{ color: C.primary, fontWeight: 600, textDecoration: "underline" }}
          >
            {label}
          </Link>
        ) : (
          <a
            key={`a-${keyIndex++}`}
            href={targetUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: C.primary, fontWeight: 600, textDecoration: "underline" }}
          >
            {label}
          </a>
        ),
      );
      remaining = remaining.slice(full.length);
      continue;
    }

    // 2. Bold: **text**
    const boldMatch = remaining.match(/^\*\*(.*?)\*\*/);
    if (boldMatch) {
      const [full, inner] = boldMatch;
      tokens.push(
        <strong key={`b-${keyIndex++}`} style={{ fontWeight: 700, color: C.navy }}>
          {inner}
        </strong>,
      );
      remaining = remaining.slice(full.length);
      continue;
    }

    // 3. Italic: *text* or _text_
    const italicMatch = remaining.match(/^(\*|_)(.*?)\1/);
    if (italicMatch) {
      const [full, , inner] = italicMatch;
      tokens.push(
        <em key={`i-${keyIndex++}`} style={{ fontStyle: "italic" }}>
          {inner}
        </em>,
      );
      remaining = remaining.slice(full.length);
      continue;
    }

    // 4. Inline code: `text`
    const codeMatch = remaining.match(/^`(.*?)`/);
    if (codeMatch) {
      const [full, inner] = codeMatch;
      tokens.push(
        <code
          key={`c-${keyIndex++}`}
          style={{
            background: "#f1f5f9",
            padding: "2px 6px",
            borderRadius: 4,
            fontSize: "0.9em",
            fontFamily: "monospace",
          }}
        >
          {inner}
        </code>,
      );
      remaining = remaining.slice(full.length);
      continue;
    }

    // Regular plain text until next special character
    const nextSpecial = remaining.search(/(\[|\*\*|\*|_|`)/);
    if (nextSpecial === -1) {
      tokens.push(remaining);
      break;
    } else if (nextSpecial === 0) {
      tokens.push(remaining[0]);
      remaining = remaining.slice(1);
    } else {
      tokens.push(remaining.slice(0, nextSpecial));
      remaining = remaining.slice(nextSpecial);
    }
  }

  return tokens;
}

/**
 * LegalContentRenderer: converts markdown / formatted text into structured semantic HTML.
 */
export function LegalContentRenderer({ content }) {
  const { search } = useLocation();
  const appView = isAppView(search, visitStorage());

  if (!content) return null;

  // Split into lines and group into blocks
  const lines = content.split("\n");
  const blocks = [];
  let currentList = null;
  let currentBlockquote = null;
  let currentParagraph = [];

  const flushParagraph = () => {
    if (currentParagraph.length > 0) {
      blocks.push({
        type: "paragraph",
        text: currentParagraph.join(" ").trim(),
      });
      currentParagraph = [];
    }
  };

  const flushList = () => {
    if (currentList) {
      blocks.push(currentList);
      currentList = null;
    }
  };

  const flushBlockquote = () => {
    if (currentBlockquote) {
      blocks.push(currentBlockquote);
      currentBlockquote = null;
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const rawLine = lines[i];
    const trimmed = rawLine.trim();

    // Blank line -> flush active blocks
    if (!trimmed) {
      flushParagraph();
      flushList();
      flushBlockquote();
      continue;
    }

    // Header 1 (# ...)
    if (trimmed.startsWith("# ")) {
      flushParagraph();
      flushList();
      flushBlockquote();
      blocks.push({ type: "h1", text: trimmed.slice(2).trim() });
      continue;
    }

    // Header 2 (## ...)
    if (trimmed.startsWith("## ")) {
      flushParagraph();
      flushList();
      flushBlockquote();
      blocks.push({ type: "h2", text: trimmed.slice(3).trim() });
      continue;
    }

    // Header 3 (### ...)
    if (trimmed.startsWith("### ")) {
      flushParagraph();
      flushList();
      flushBlockquote();
      blocks.push({ type: "h3", text: trimmed.slice(4).trim() });
      continue;
    }

    // Blockquote (> ...)
    if (trimmed.startsWith(">")) {
      flushParagraph();
      flushList();
      const quoteText = trimmed.replace(/^>\s*/, "");
      if (!currentBlockquote) {
        currentBlockquote = { type: "blockquote", lines: [quoteText] };
      } else {
        currentBlockquote.lines.push(quoteText);
      }
      continue;
    } else {
      flushBlockquote();
    }

    // Unordered list (- ... or * ...)
    if (/^[-*]\s+/.test(trimmed)) {
      flushParagraph();
      const itemText = trimmed.replace(/^[-*]\s+/, "");
      if (!currentList || currentList.ordered) {
        flushList();
        currentList = { type: "list", ordered: false, items: [itemText] };
      } else {
        currentList.items.push(itemText);
      }
      continue;
    }

    // Ordered list (1. ...)
    if (/^\d+\.\s+/.test(trimmed)) {
      flushParagraph();
      const itemText = trimmed.replace(/^\d+\.\s+/, "");
      if (!currentList || !currentList.ordered) {
        flushList();
        currentList = { type: "list", ordered: true, items: [itemText] };
      } else {
        currentList.items.push(itemText);
      }
      continue;
    }

    // Otherwise, plain paragraph line
    flushList();
    currentParagraph.push(trimmed);
  }

  flushParagraph();
  flushList();
  flushBlockquote();

  return (
    <div
      className="legal-content-body"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 20,
        fontSize: 15,
        color: C.textDark,
        lineHeight: 1.7,
      }}
    >
      {blocks.map((block, idx) => {
        switch (block.type) {
          case "h1":
            return (
              <h1
                key={idx}
                style={{
                  fontSize: 26,
                  fontWeight: 800,
                  color: C.navy,
                  marginTop: 24,
                  marginBottom: 8,
                  letterSpacing: "-0.4px",
                }}
              >
                {renderInlineMarkdown(block.text, appView)}
              </h1>
            );
          case "h2":
            return (
              <h2
                key={idx}
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: C.navy,
                  marginTop: 20,
                  marginBottom: 6,
                  letterSpacing: "-0.2px",
                }}
              >
                {renderInlineMarkdown(block.text, appView)}
              </h2>
            );
          case "h3":
            return (
              <h3
                key={idx}
                style={{
                  fontSize: 17,
                  fontWeight: 700,
                  color: C.navy,
                  marginTop: 14,
                  marginBottom: 4,
                }}
              >
                {renderInlineMarkdown(block.text, appView)}
              </h3>
            );
          case "paragraph":
            return (
              <p key={idx} style={{ margin: 0 }}>
                {renderInlineMarkdown(block.text, appView)}
              </p>
            );
          case "blockquote":
            return (
              <div
                key={idx}
                style={{
                  borderLeft: `4px solid ${C.primary}`,
                  background: C.bgMuted || "#f8fafc",
                  padding: "12px 18px",
                  borderRadius: "0 8px 8px 0",
                  fontSize: 14,
                  margin: "8px 0",
                }}
              >
                {block.lines.map((l, lIdx) => (
                  <p key={lIdx} style={{ margin: lIdx > 0 ? "8px 0 0" : 0 }}>
                    {renderInlineMarkdown(l, appView)}
                  </p>
                ))}
              </div>
            );
          case "list":
            return block.ordered ? (
              <ol
                key={idx}
                style={{
                  paddingLeft: 24,
                  margin: "4px 0",
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                {block.items.map((it, itIdx) => (
                  <li key={itIdx}>{renderInlineMarkdown(it, appView)}</li>
                ))}
              </ol>
            ) : (
              <ul
                key={idx}
                style={{
                  paddingLeft: 24,
                  margin: "4px 0",
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                {block.items.map((it, itIdx) => (
                  <li key={itIdx}>{renderInlineMarkdown(it, appView)}</li>
                ))}
              </ul>
            );
          default:
            return null;
        }
      })}
    </div>
  );
}
